import React, { useState, useEffect } from "react";
import { getScoped, setScoped, removeScoped } from '@/lib/userScopedStorage';
import { useNavigate } from "react-router-dom";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Eye, ChevronRight, ChevronLeft, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { MainLayout } from "@/components/layout/MainLayout";
import { getUserSettings, saveUserSettings } from "@/lib/userSettings";
import { Slider } from "@/components/ui/slider";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";

// Gesture definitions - labels will be translated in component
type GestureId = "look_up" | "look_down" | "look_left" | "look_right" | "blink";

const gestureIcons: Record<GestureId, React.ElementType> = {
  look_up: ArrowUp,
  look_down: ArrowDown,
  look_left: ArrowLeft,
  look_right: ArrowRight,
  blink: Eye,
};

// Gesture button component - matches onboarding design
const GestureButton = ({ 
  id, 
  isSelected, 
  onToggle,
  label,
  isRTL
}: { 
  id: GestureId; 
  isSelected: boolean; 
  onToggle: () => void;
  label: string;
  isRTL: boolean;
}) => {
  const Icon = gestureIcons[id];
  
  return (
    <button
      onClick={onToggle}
      dir={isRTL ? "rtl" : "ltr"}
      className={cn(
        "flex items-center gap-3 p-3 rounded-xl transition-all",
        "border-2 cursor-pointer w-[220px]",
        isSelected
          ? "bg-primary/10 border-primary shadow-md"
          : "bg-card border-border hover:border-primary/30"
      )}
    >
      {/* Checkbox - fixed size */}
      <div className={cn(
        "w-6 h-6 rounded border-2 flex items-center justify-center transition-all flex-shrink-0",
        isSelected
          ? "bg-primary border-primary"
          : "bg-background border-muted-foreground/40"
      )}>
        {isSelected && (
          <Check className="w-4 h-4 text-primary-foreground stroke-[3]" />
        )}
      </div>

      {/* Icon - fixed size */}
      <div className={cn(
        "w-9 h-9 rounded-full flex items-center justify-center transition-colors flex-shrink-0",
        isSelected ? "bg-slate-800" : "bg-muted"
      )}>
        <Icon className={cn(
          "w-5 h-5 transition-colors",
          isSelected ? "text-accent" : "text-muted-foreground"
        )} />
      </div>

      {/* Label - fixed width for alignment */}
      <span className={cn(
        "text-sm font-medium w-[110px] whitespace-nowrap flex-shrink-0",
        isRTL ? "text-right" : "text-left",
        isSelected ? "text-primary" : "text-foreground"
      )}>
        {label}
      </span>
    </button>
  );
};

export default function EyeControlSettings() {
  const navigate = useNavigate();
  const { t, dir, isRTL } = useCaregiverTranslation();
  const [selectedGestures, setSelectedGestures] = useState<string[]>([]);
  const [scanMode, setScanMode] = useState<"cell_by_cell" | "row_and_column">("cell_by_cell");
  const [scanSpeed, setScanSpeed] = useState(3.0);
  const [scanControlMode, setScanControlMode] = useState<"automatic" | "gesture_controlled">("automatic");

  // Load saved settings
  useEffect(() => {
    const settings = getUserSettings();
    if (settings.eyeControlGestures && settings.eyeControlGestures.length > 0) {
      setSelectedGestures(settings.eyeControlGestures);
    }
    setScanMode(settings.scanMode || "cell_by_cell");
    setScanSpeed(settings.scanSpeed || 3.0);
    setScanControlMode(settings.scanControlMode || "automatic");
  }, []);

  const settings = getUserSettings();
  const isFemale = settings.gender === "female";

  // cell-by-cell is only available when 5 gestures selected AND gesture_controlled
  const isCellByCellAvailable = selectedGestures.length === 5 && scanControlMode === 'gesture_controlled';

  // Auto-switch scanMode to row_and_column when cell_by_cell becomes unavailable.
  // Guard: only run after gestures are loaded (length > 0) to avoid overwriting
  // saved cell_by_cell on the initial render when gestures haven't loaded yet.
  useEffect(() => {
    if (selectedGestures.length === 0) return; // Gestures not loaded yet
    if (!isCellByCellAvailable && scanMode === 'cell_by_cell') {
      setScanMode('row_and_column');
      saveUserSettings({ ...getUserSettings(), scanMode: 'row_and_column' });
    }
  }, [isCellByCellAvailable, scanMode, selectedGestures.length]);

  // Auto-switch scanControlMode to 'automatic' when gestures drop below 4
  useEffect(() => {
    if (selectedGestures.length < 4 && scanControlMode === 'gesture_controlled') {
      setScanControlMode('automatic');
      saveUserSettings({ ...getUserSettings(), scanControlMode: 'automatic' });
    }
  }, [selectedGestures.length, scanControlMode]);

  // Gesture labels using translations
  const gestureLabels: Record<GestureId, string> = {
    look_up: t("lookUp"),
    look_down: t("lookDown"),
    look_left: t("lookLeft"),
    look_right: t("lookRight"),
    blink: t("blink"),
  };

  const toggleGesture = (gestureId: string) => {
    setSelectedGestures(prev => {
      const newGestures = prev.includes(gestureId)
        ? prev.filter(g => g !== gestureId)
        : [...prev, gestureId];
      
      // Auto-set scan control mode based on gesture count:
      // 4+ gestures = gesture_controlled, 3 or less = automatic
      const newScanControlMode = newGestures.length >= 4 ? "gesture_controlled" : "automatic";
      setScanControlMode(newScanControlMode);
      
      const settings = getUserSettings();
      saveUserSettings({
        ...settings,
        eyeControlGestures: newGestures,
        scanControlMode: newScanControlMode
      });

      // Update scanning config with new input count
      const currentConfig = getScoped('ayuni_scanning_config');
      const config = currentConfig ? JSON.parse(currentConfig) : { pattern: 'LINEAR', skipEmpty: true, inputCount: 1 };
      config.inputCount = newGestures.length;
      setScoped('ayuni_scanning_config', JSON.stringify(config));
      window.dispatchEvent(new CustomEvent('scanningConfigChanged', { detail: config }));

      return newGestures;
    });
  };

  const handleScanModeChange = (mode: "cell_by_cell" | "row_and_column") => {
    setScanMode(mode);
    const settings = getUserSettings();
    saveUserSettings({ ...settings, scanMode: mode });

    // Also update scanning config immediately (same pattern as handleScanSpeedChange)
    const currentConfig = getScoped('ayuni_scanning_config');
    const config = currentConfig ? JSON.parse(currentConfig) : { pattern: 'LINEAR', skipEmpty: true, inputCount: 1 };
    config.pattern = mode === 'row_and_column' ? 'ROW_COLUMN' : 'LINEAR';
    setScoped('ayuni_scanning_config', JSON.stringify(config));
    window.dispatchEvent(new CustomEvent('scanningConfigChanged', { detail: config }));
  };

  const handleScanSpeedChange = (value: number[]) => {
    const speed = value[0];
    setScanSpeed(speed);
    const settings = getUserSettings();
    saveUserSettings({ ...settings, scanSpeed: speed });
    
    // Also update scanning config immediately
    const currentConfig = getScoped('ayuni_scanning_config');
    const config = currentConfig ? JSON.parse(currentConfig) : { pattern: 'LINEAR', skipEmpty: true, inputCount: 1 };
    config.speed = speed * 1000; // Convert to milliseconds
    setScoped('ayuni_scanning_config', JSON.stringify(config));
    window.dispatchEvent(new CustomEvent('scanningConfigChanged', { detail: config }));
  };

  const handleScanControlModeChange = (mode: "automatic" | "gesture_controlled") => {
    setScanControlMode(mode);
    const settings = getUserSettings();
    saveUserSettings({ ...settings, scanControlMode: mode });

    // Also update scanning config immediately
    const currentConfig = getScoped('ayuni_scanning_config');
    const config = currentConfig ? JSON.parse(currentConfig) : { pattern: 'LINEAR', skipEmpty: true, inputCount: 1 };
    setScoped('ayuni_scanning_config', JSON.stringify(config));
    window.dispatchEvent(new CustomEvent('scanningConfigChanged', { detail: config }));
  };

  const handleBack = () => {
    navigate("/control");
  };

  const handleContinue = () => {
    // Save settings first
    const settings = getUserSettings();
    saveUserSettings({ 
      ...settings, 
      controlMethod: "eye_control",
      eyeControlGestures: selectedGestures,
      scanMode,
      scanSpeed,
      scanControlMode
    });
    
    // Navigate to action mapping screen
    navigate("/control/eye-control/mapping");
  };

  // Match external switch descriptions exactly
  const gestureCountDescriptionKeys: Record<number, string> = {
    1: "gesture1Desc",
    2: "gesture2Desc",
    3: "gesture3Desc",
    4: "switch4Desc",
    5: "switch5Desc",
  };

  const getScanningModeDescription = () => {
    const key = gestureCountDescriptionKeys[selectedGestures.length];
    return key ? t(key as any) : "";
  };

  return (
    <MainLayout>
      <div 
        className="flex-1 flex flex-col items-center p-4 bg-background overflow-auto"
        dir={dir}
      >
        {/* Header with icon */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-lg">
            <Eye className="w-6 h-6 text-primary-foreground" />
          </div>
          <h1 className="text-xl font-bold text-foreground">
            {t("controlSetup")}
          </h1>
        </div>

        {/* Subtitle */}
        <p className="text-sm text-muted-foreground text-center mb-3">
          {isFemale ? t("selectGesturesFemale") : t("selectGesturesMale")}
        </p>

        {/* Gestures in Plus/Cross Shape */}
        <div className="flex flex-col items-center gap-2 mb-4">
          {/* Top: Look Up */}
          <GestureButton 
            id="look_up" 
            isSelected={selectedGestures.includes("look_up")}
            onToggle={() => toggleGesture("look_up")}
            label={gestureLabels.look_up}
            isRTL={isRTL}
          />
          
          {/* Middle Row - Physical position fixed: left on screen-left, right on screen-right */}
          {/* Use dir="ltr" to prevent RTL from flipping the order */}
          <div className="flex items-center gap-2" dir="ltr">
            <GestureButton 
              id="look_left" 
              isSelected={selectedGestures.includes("look_left")}
              onToggle={() => toggleGesture("look_left")}
              label={gestureLabels.look_left}
              isRTL={isRTL}
            />
            <GestureButton 
              id="blink" 
              isSelected={selectedGestures.includes("blink")}
              onToggle={() => toggleGesture("blink")}
              label={gestureLabels.blink}
              isRTL={isRTL}
            />
            <GestureButton 
              id="look_right" 
              isSelected={selectedGestures.includes("look_right")}
              onToggle={() => toggleGesture("look_right")}
              label={gestureLabels.look_right}
              isRTL={isRTL}
            />
          </div>
          
          {/* Bottom: Look Down */}
          <GestureButton 
            id="look_down" 
            isSelected={selectedGestures.includes("look_down")}
            onToggle={() => toggleGesture("look_down")}
            label={gestureLabels.look_down}
            isRTL={isRTL}
          />
        </div>

        {/* Scanning Settings - Only show when at least 1 gesture selected */}
        {selectedGestures.length > 0 && (
          <>
            {/* Selected Mode Info */}
            <div className="w-full max-w-2xl bg-primary/10 border-2 border-primary/30 rounded-xl p-3 mb-4">
              <p className="text-sm text-muted-foreground text-center">
                {getScanningModeDescription()}
              </p>
            </div>

            {/* Scan Control Mode - Only for 4+ gestures */}
            {selectedGestures.length >= 4 && (
              <div className="w-full max-w-2xl mb-4">
                <p className="text-sm text-muted-foreground text-right mb-2">
                  {isFemale ? t("selectScanTypeFemale") : t("selectScanTypeMale")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleScanControlModeChange("automatic")}
                    className={cn(
                      "p-3 rounded-xl border-2 transition-all text-sm",
                      scanControlMode === "automatic"
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card border-border hover:border-primary/30"
                    )}
                  >
                    {t("automaticScanning")}
                  </button>
                  <button
                    onClick={() => handleScanControlModeChange("gesture_controlled")}
                    className={cn(
                      "p-3 rounded-xl border-2 transition-all text-sm",
                      scanControlMode === "gesture_controlled"
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card border-border hover:border-primary/30"
                    )}
                  >
                    {t("gestureScanning")}
                  </button>
                </div>
              </div>
            )}

            {/* Scan Mode Section */}
            <div className="w-full max-w-2xl mb-4">
              <p className="text-sm text-muted-foreground text-right mb-2">{t("scanMode")}</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => isCellByCellAvailable && handleScanModeChange("cell_by_cell")}
                  disabled={!isCellByCellAvailable}
                  className={cn(
                    "flex flex-col items-center p-3 rounded-xl border-2 transition-all",
                    scanMode === "cell_by_cell" && isCellByCellAvailable
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card border-border hover:border-primary/30 text-foreground",
                    !isCellByCellAvailable && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <span className="font-medium text-sm">{t("cellByCellMode")}</span>
                  <span className={cn(
                    "text-xs mt-1",
                    scanMode === "cell_by_cell" ? "text-primary-foreground/70" : "text-muted-foreground"
                  )}>
                    {t("cellByCellDesc")}
                  </span>
                </button>
                <button
                  onClick={() => handleScanModeChange("row_and_column")}
                  className={cn(
                    "flex flex-col items-center p-3 rounded-xl border-2 transition-all",
                    scanMode === "row_and_column"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card border-border hover:border-primary/30 text-foreground"
                  )}
                >
                  <span className="font-medium text-sm">{t("rowAndColumnMode")}</span>
                  <span className={cn(
                    "text-xs mt-1",
                    scanMode === "row_and_column" ? "text-primary-foreground/70" : "text-muted-foreground"
                  )}>
                    {t("rowAndColumnDesc")}
                  </span>
                </button>
              </div>
            </div>

            {/* Scan Speed Slider - Show for all automatic scanning modes (1-2 gestures are always auto) */}
            {selectedGestures.length >= 1 && (scanControlMode === "automatic" || selectedGestures.length <= 2) && (
              <div className="w-full max-w-2xl mb-6">
                <p className="text-sm text-muted-foreground text-right mb-2">{t("scanSpeedLabel")}</p>
                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">{t("slow")}</span>
                    <span className="text-sm font-medium text-foreground">{scanSpeed.toFixed(1)}s</span>
                    <span className="text-xs text-muted-foreground">{t("fast")}</span>
                  </div>
                  <Slider
                    value={[scanSpeed]}
                    onValueChange={handleScanSpeedChange}
                    min={0.5}
                    max={5}
                    step={0.5}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Action buttons - Back on left in LTR, Continue on right */}
        <div className="flex gap-3 mt-auto pb-4">
          <button
            onClick={handleBack}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
              "bg-card hover:bg-muted border-2 border-border",
              "text-muted-foreground hover:text-foreground"
            )}
          >
            {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            <span>{t("back")}</span>
          </button>

          <button
            onClick={handleContinue}
            disabled={selectedGestures.length === 0}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
              "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg",
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
            )}
          >
            <Check className="w-5 h-5" />
            <span>{t("continueButton")}</span>
          </button>
        </div>
      </div>
    </MainLayout>
  );
}
